import { ActionIcon, Box, Button, Divider, Group, SimpleGrid, Stack, Text } from '@mantine/core';
import { schemaResolver } from '@mantine/form';
import { modals } from '@mantine/modals';
import { IconPlus } from '@tabler/icons-react';
import { ClientForm } from 'app/Private/Clients/Form';
import { Right } from 'components/index';
import dayjs from 'dayjs';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { tKeys, useTranslation } from 'locales';
import { useCallback } from 'react';
import InvoiceFooter from 'shared/components/InvoiceFooter';
import { getCgv, useGetConstants } from 'shared/utils/selectors';
import type { Debtor } from 'types/store/client-state';

import {
  BillingLogField,
  CommentField,
  CompanyDetails,
  DebtorDetails,
  DebtorField,
  DueDateField,
  IssueDateField,
  OrderReferenceField,
  TotalsDetails,
} from './components';
import { InvoiceFormProvider, useInvoiceForm } from './InvoiceForm.context';
import { InvoiceFormSubmitSchema } from './InvoiceForm.schema';
import type {
  InvoiceFormFieldValues,
  InvoiceFormProps,
  InvoiceFormSharedProps,
} from './InvoiceForm.types';
import {
  getCurrency,
  getDefaultDueDate,
  getDefaultIssueDate,
  isNoTva,
  toFormDate,
  toFormDateOptional,
} from './InvoiceForm.utils';
import { InvoiceFormContainer } from './InvoiceFormContainer';
import InvoiceLines from './InvoiceLines';

export const InvoiceForm = (props: InvoiceFormProps) => {
  const { initialValues = {}, isCreditNote = false, creditNote, invoice } = props;
  const initialIssueDate = toFormDateOptional(initialValues.issue_date);

  const { t } = useTranslation();
  const constants = useGetConstants();
  const company = useCompanyConfiguration();

  const validate = schemaResolver(InvoiceFormSubmitSchema, { sync: true });

  const formInitialValues: InvoiceFormFieldValues = {
    ...initialValues,
    issue_date: initialIssueDate ?? toFormDate(getDefaultIssueDate()),
    due_date: toFormDateOptional(initialValues.due_date) ?? toFormDate(getDefaultDueDate(company)),
    product_lines_attributes: initialValues.product_lines_attributes ?? [],
  };

  const form = useInvoiceForm({
    mode: 'controlled',
    initialValues: formInitialValues,
    validate,
  });

  const debtor = form.values.debtor_attributes;
  const setFieldValue = form.setFieldValue;

  const noTva = isNoTva(debtor);
  const currency = getCurrency(company, debtor, invoice || creditNote);

  // Computes the due_date from a chosen issue_date and the active CGV's
  // payment-terms delay. Used by both the issue-date picker and the
  // debtor-select handlers — the only inputs that legitimately reset due_date.
  // Triggering this from a useEffect would create a render→setState loop in
  // Mantine's controlled mode (callback refs aren't guaranteed stable, so the
  // effect re-fires every render, even when the inputs haven't changed).
  const computeDueDate = useCallback(
    (issueDate: string | null, forDebtor: Debtor | undefined) => {
      if (!issueDate) return null;
      const delay = getCgv(company, forDebtor)?.minimum_payment_terms_delay || 0;
      if (!delay) return null;
      return toFormDate(dayjs(issueDate).add(delay, 'day'));
    },
    [company],
  );

  const handleIssueDateChange = useCallback(
    (newIssueDate: string | null) => {
      setFieldValue('issue_date', newIssueDate);
      const nextDueDate = computeDueDate(newIssueDate, debtor);
      if (nextDueDate) setFieldValue('due_date', nextDueDate);
    },
    [computeDueDate, debtor, setFieldValue],
  );

  const handleSelectDebtor = useCallback(
    (selected: Debtor) => {
      setFieldValue('debtor_attributes', selected);

      // When the new debtor is noTva, force every existing non-invoice line's
      // tax_category_code back to '00' so the running totals reflect the
      // selected debtor's VAT regime. Newly added lines start at '00' via
      // getProductLine(noTva), so this only matters on debtor switch.
      if (isNoTva(selected)) {
        form.values.product_lines_attributes?.forEach((line, index) => {
          if (line.isInvoice) return;
          if (line.product_attributes?.tax_category_code === '00') return;
          setFieldValue(
            `product_lines_attributes.${index}.product_attributes.tax_category_code`,
            '00',
          );
        });
      }

      // Recompute due_date with the new debtor's CGV delay.
      const nextDueDate = computeDueDate(form.values.issue_date, selected);
      if (nextDueDate) setFieldValue('due_date', nextDueDate);
    },
    [computeDueDate, form.values.issue_date, form.values.product_lines_attributes, setFieldValue],
  );

  const handleAddDebtor = () => {
    modals.open({
      children: (
        <>
          <ClientForm
            // Can't do better than any as long as we don't have a useLoadDebtor hook
            onSuccess={(val: any) => {
              setFieldValue('debtor_attributes', val.data_attributes);
            }}
          />
          <Right>
            <Button color="warning" variant="subtle" onClick={modals.closeAll}>
              {t(tKeys.CANCEL)}
            </Button>
            {/* The client form will render its save button to this outlet */}
            <div id="save-client-button-outlet" />
          </Right>
        </>
      ),
      withCloseButton: false,
      size: 'auto',
    });
  };

  const sharedProps = {
    constants,
    company,
    isCreditNote,
    currency,
    noTva,
  } as InvoiceFormSharedProps;

  return (
    <InvoiceFormProvider form={form}>
      <InvoiceFormContainer {...props} {...sharedProps}>
        <Stack gap="md">
          {company.invoice_customization.logo_url && (
            <Box
              component="img"
              src={company.invoice_customization.logo_url}
              alt=""
              miw={100}
              maw={140}
            />
          )}
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <CompanyDetails {...sharedProps} />
            <Stack>
              <Group align="start" justify="end">
                <Box w="80%">
                  <DebtorField onSelectDebtor={handleSelectDebtor} />
                </Box>
                <ActionIcon variant="light" onClick={handleAddDebtor}>
                  <IconPlus size={16} stroke={1.5} />
                </ActionIcon>
              </Group>
              {debtor && <DebtorDetails {...sharedProps} debtor={debtor} />}
            </Stack>
          </SimpleGrid>
          <Divider />
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <Stack gap="xs">
              <Text>{t(tKeys.INVOICING.CREATE.BILLING_BOOK)}</Text>
              <BillingLogField {...sharedProps} />
              <OrderReferenceField />
            </Stack>
            <Stack align="flex-end" gap="xs">
              <Group gap="xs">
                <Text>{t(tKeys.DATE)}:</Text>
                <IssueDateField onChange={handleIssueDateChange} />
              </Group>
              <Group gap="xs">
                <Text>{t(tKeys.DUE_DATE)}:</Text>
                <DueDateField {...sharedProps} />
              </Group>
            </Stack>
          </SimpleGrid>
          <InvoiceLines {...sharedProps} initialValues={initialValues} />
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <CommentField />
            <TotalsDetails {...sharedProps} />
          </SimpleGrid>
          <InvoiceFooter />
        </Stack>
      </InvoiceFormContainer>
    </InvoiceFormProvider>
  );
};
